import { handleFocus } from "../../utils/focus";
import { CLASS_NAMES } from "./constants";
import { ModalListener } from "./ModalListener";

export class ModalHandler {
  hiddenElements: HTMLElement[] = [];
  constructor() {
    const header: HTMLElement[] = [...document.getElementsByTagName("header")];
    const footer: HTMLElement[] = [...document.getElementsByTagName("footer")];
    const main: HTMLElement[] = [...document.getElementsByTagName("main")];
    this.hiddenElements = [...header, ...footer, ...main];
  }
  async handleClick(
    this: HTMLElement,
    modal: HTMLElement | null,
    buttons: (HTMLElement | null)[],
    handler: ModalListener,
    e: Event
  ) {
    e.preventDefault();
    e.stopPropagation();

    if (modal) {
      /**
       * @type {boolean} the modal is active or not active NOW
       */
      const isActive: boolean = this.ariaExpanded === "true" || modal.classList.contains("is-active");
      await handler
        .handleModal(modal, buttons, isActive)
        .then(() => (isActive ? handler.unlisten() : handler.listenClose()))
        .finally(() => isActive && handler.resetScroll(modal));
    } else return;
  }

  /**
   * @method handleKeyDown
   * @param {HTMLElement} this `js-modal-button`
   * @param {(HTMLElement | null)} modal `.js-modal`
   * @param {(HTMLElement | null)[]} buttons
   * @param {ModalListener} handler
   * @param {KeyboardEvent} e
   */
  handleKeyDown(
    this: HTMLElement,
    modal: HTMLElement | null,
    buttons: (HTMLElement | null)[],
    handler: ModalListener,
    e: KeyboardEvent
  ) {
    modal &&
      handleFocus(e, modal, () => {
        handler.handleModal(modal, buttons, true);
      });
  }

  /**
   * @async
   * @method handleModal
   * @param {HTMLElement} modal
   * @param {boolean} isActive
   * @returns {Promise<void>}
   */
  async handleModal(modal: HTMLElement, buttons: (HTMLElement | null)[], isActive: boolean): Promise<void> {
    this.hiddenElements.forEach((element: Element) => element.setAttribute("aria-hidden", (!isActive).toString()));
    buttons.forEach((button) => button && (button.ariaExpanded = (!isActive === true).toString()));
    modal.classList.toggle("is-active", !isActive);
    document.body.classList.toggle("is-scrollLock", !isActive);
    setTimeout(
      () => {
        if (modal instanceof HTMLDialogElement) {
          isActive !== true ? modal.showModal() : modal.close();
        } else {
          modal.ariaHidden = isActive.toString();
        }
      },
      isActive ? 500 : 0
    );
  }

  async handleAnchor(
    this: HTMLAnchorElement,
    modal: HTMLElement,
    buttons: (HTMLElement | null)[],
    handler: ModalListener,
    e: Event
  ) {
    e.preventDefault();
    const href: string = this.href.split("#")[1];
    const target: HTMLElement | null = document.getElementById(href);
    if (target) {
      target.scrollIntoView({ behavior: "auto", inline: "start", block: "start" });
      await handler.close(modal, buttons, handler, e);
    }
  }

  /**
   * @method close close current modal
   * @param {(HTMLElement | null)} modal
   * @param {(HTMLElement | null)[]} buttons
   * @param {ModalListener} handler
   */
  async close(modal: HTMLElement | null, buttons: (HTMLElement | null)[], handler: ModalListener, e: Event) {
    const target = e.target;
    if (modal) {
      if (
        target instanceof HTMLElement &&
        (target.classList.contains(CLASS_NAMES.BACKDROP) || e.currentTarget instanceof HTMLAnchorElement)
      ) {
        handler.handleModal(modal, buttons, true);
        handler.unlisten();
      }
      handler.resetScroll(modal);
    } else return console.error("modal is null");
  }

  resetScroll(modal: HTMLElement) {
    const scrollResetElements = modal.getElementsByClassName("js-scrollTop0");

    for (let i = 0; i < scrollResetElements.length; i++) {
      const element = scrollResetElements[i];
      element.scroll(0, 0);
    }
  }
}
